import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidePanel } from 'design-system-web';
import { TranslationKeys } from 'localization';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { useAssignableMembers } from 'lane-shared/domains/workOrder/hooks/useAssignableMembers';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import { ALL_ISSUE_TYPES_VALUE } from './coverageHoursPanelUtils';
import { CreateCoverageHours } from './CreateCoverageHours';
import { CoverageHoursPanelFooter } from './CoverageHoursPanelFooter';

const TRANSLATION_KEYS: Record<'title', TranslationKeys> = Object.freeze({
  title: 'web.admin.serviceRequest.settings.coverageHours.create.title',
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channelId: string;
  categories: WorkOrderModuleCategoriesSettings[];
};

export function CreateCoverageHoursPanel({
  isOpen,
  onClose,
  channelId,
  categories,
}: Props) {
  const { t } = useTranslation();
  const [, , assignableTeams] = useAssignableMembers(channelId);
  const [categoryValue, setCategoryValue] = useState<string | null>(null);
  const [issueTypeValue, setIssueTypeValue] = useState<string>(
    ALL_ISSUE_TYPES_VALUE
  );
  const [savedTeams, setSavedTeams] = useState<TeamCoverageEntry[]>([]);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);

  const hasUnsavedChanges = savedTeams.length > 0;

  useEffect(() => {
    if (!isOpen) {
      setIsConfirmingClose(false);
    }
  }, [isOpen]);

  const handleCategoryChange = (value: string) => {
    setCategoryValue(value);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
  };

  const handleClose = () => {
    setCategoryValue(null);
    setIssueTypeValue(ALL_ISSUE_TYPES_VALUE);
    setSavedTeams([]);
    setIsConfirmingClose(false);
    onClose();
  };

  const handleSaveTeam = (entry: TeamCoverageEntry) => {
    setSavedTeams(prev => {
      const idx = prev.findIndex(team => team.id === entry.id);

      if (idx >= 0) {
        const next = [...prev];

        next[idx] = entry;

        return next;
      }

      return [...prev, entry];
    });
  };

  const handleOnCancel = () => {
    if (hasUnsavedChanges) {
      setIsConfirmingClose(true);
    } else {
      handleClose();
    }
  };

  return (
    <SidePanel
      id="create-coverage-hours-panel"
      isOpen={isOpen}
      onClose={handleClose}
      header={t(TRANSLATION_KEYS.title)}
      size="medium"
      backdropEnabled
      footerActions={
        <CoverageHoursPanelFooter
          isConfirmingClose={isConfirmingClose}
          onDiscardChanges={handleClose}
          onKeepEditing={() => setIsConfirmingClose(false)}
          onCancel={handleOnCancel}
        />
      }
    >
      <CreateCoverageHours
        categories={categories}
        categoryValue={categoryValue}
        onCategoryChange={handleCategoryChange}
        issueTypeValue={issueTypeValue}
        onIssueTypeChange={setIssueTypeValue}
        assignableTeams={assignableTeams}
        savedTeams={savedTeams}
        onSaveTeam={handleSaveTeam}
      />
    </SidePanel>
  );
}
